import { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchJurisdictionHistory, type HistoryPoint } from '@/services/scoreService';
import { AlertCircle } from 'lucide-react';

interface TrendChartProps {
  jurisdictionCode: string;
  jurisdictionName: string;
}

// Status threshold lines
const THRESHOLDS = [
  { value: 35, color: '#3d9970', label: 'Stable' },
  { value: 55, color: '#e6a23c', label: 'Watch' },
  { value: 75, color: '#d4712a', label: 'Tightening' },
];

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getStatusLabel(score: number): string {
  if (score >= 75) return 'High Pressure';
  if (score >= 55) return 'Tightening';
  if (score >= 35) return 'Watch';
  return 'Stable';
}

function getScoreColor(score: number): string {
  if (score < 35) return '#3d9970';
  if (score < 55) return '#e6a23c';
  if (score < 75) return '#d4712a';
  return '#c9524a';
}

export default function TrendChart({ jurisdictionCode, jurisdictionName }: TrendChartProps) {
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchJurisdictionHistory(jurisdictionCode, 30);
        setHistory(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load history');
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [jurisdictionCode]);

  // Calculate days covered by the data
  const { daysCovered, chartTitle, isSparseData } = useMemo(() => {
    if (history.length < 2) {
      return { daysCovered: 0, chartTitle: 'Pressure Trend', isSparseData: true };
    }
    const firstDate = new Date(history[0].date);
    const lastDate = new Date(history[history.length - 1].date);
    const days = Math.ceil((lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24));
    const title = days >= 25 ? '30-Day Pressure Trend' : days >= 7 ? `${days}-Day Pressure Trend` : 'Recent Pressure Trend';
    return { daysCovered: days, chartTitle: title, isSparseData: history.length < 14 };
  }, [history]);

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-text-primary">
            Pressure Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[180px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-text-primary">
            Pressure Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[180px] flex items-center justify-center text-text-secondary text-sm">
            Unable to load trend data
          </div>
        </CardContent>
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-text-primary">
            Pressure Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[180px] flex items-center justify-center text-text-secondary text-sm">
            No historical data available yet
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate min/max for Y axis with padding
  const scores = history.map(h => h.score);
  const minScore = Math.max(0, Math.min(...scores) - 10);
  const maxScore = Math.min(100, Math.max(...scores) + 10);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-sm font-semibold text-text-primary">
              {chartTitle}
            </CardTitle>
            <p className="text-xs text-text-tertiary mt-0.5">
              Higher score = more regulatory pressure
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pb-3">
        <ResponsiveContainer width="100%" height={180}>
          <LineChart
            data={history}
            margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
          >
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: '#888' }}
              tickFormatter={formatDate}
              tickLine={false}
              axisLine={{ stroke: '#333' }}
              interval="preserveStartEnd"
            />
            <YAxis
              domain={[minScore, maxScore]}
              tick={{ fontSize: 10, fill: '#888' }}
              tickLine={false}
              axisLine={{ stroke: '#333' }}
              width={40}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(26, 26, 26, 0.95)',
                border: '1px solid #333',
                borderRadius: '6px',
                padding: '8px 12px',
              }}
              labelStyle={{ color: '#888', fontSize: 12, marginBottom: 4 }}
              itemStyle={{ color: '#fff', fontSize: 14, fontWeight: 500 }}
              formatter={(value: number) => {
                const status = getStatusLabel(value);
                return [`${value.toFixed(0)} - ${status}`, 'Pressure'];
              }}
              labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })}
            />

            {/* Status threshold reference lines */}
            {THRESHOLDS.map((threshold) => (
              <ReferenceLine
                key={threshold.value}
                y={threshold.value}
                stroke={threshold.color}
                strokeDasharray="4 4"
                strokeOpacity={0.5}
              />
            ))}

            {/* Main score line - show dots when data is sparse */}
            <Line
              type="monotone"
              dataKey="score"
              stroke="#3B82F6"
              strokeWidth={2}
              dot={isSparseData ? { r: 3, fill: '#3B82F6' } : false}
              activeDot={{
                r: 4,
                fill: '#3B82F6',
                stroke: '#fff',
                strokeWidth: 2,
              }}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Sparse data warning */}
        {isSparseData && (
          <div className="flex items-center justify-center gap-1.5 mt-2 text-xs text-amber-500/80">
            <AlertCircle className="h-3 w-3" />
            <span>Limited history: {history.length} data points</span>
          </div>
        )}

        {/* Legend with threshold zones */}
        <div className="flex items-center justify-center gap-4 mt-2 text-xs text-text-tertiary">
          {THRESHOLDS.map((threshold) => (
            <div key={threshold.value} className="flex items-center gap-1.5">
              <div
                className="w-3 h-0.5"
                style={{ backgroundColor: threshold.color }}
              />
              <span>{threshold.label}</span>
            </div>
          ))}
        </div>

        {/* Trend direction explanation */}
        <div className="flex items-center justify-center gap-4 mt-1.5 text-[10px] text-text-tertiary/70">
          <span>Rising = increasing pressure</span>
          <span className="text-text-tertiary/40">|</span>
          <span>Falling = improving conditions</span>
        </div>
      </CardContent>
    </Card>
  );
}
